import { ConfigSubdomain } from "@/configuration/tenant-ids";
import {
  buildReferralAuthOnFileMessage,
  type ReferralAuthOnFileSnapshot,
} from "@/src/lib/prior-authorization/referral-auth-on-file";
import type { Coverage } from "@/src/models/coverage";

export const VA_RFS_FORM_URL = "https://www.va.gov/vaforms/medical/pdf/VA%20Form%2010-10172.pdf";

export enum UsrcCpsAuthPortalPath {
  KaiserCoNoAction = "kaiser_co_no_action",
  VaTriWestTricare = "va_triwest_tricare",
  UhcOptum = "uhc_optum",
  Availity = "availity",
}

export type UsrcCpsReferralAuthInstructionContent = {
  text: string;
  showVaFormLink: boolean;
};

function normalizeCoverageText(value: string | null | undefined): string {
  return value?.trim().toLowerCase() ?? "";
}

function coverageSearchText(coverage: Coverage | null | undefined): string {
  if (!coverage) return "";

  return [coverage.payer, coverage.plan, coverage.customerPayer].map(normalizeCoverageText).filter(Boolean).join(" ");
}

function matchesAny(haystack: string, patterns: RegExp[]): boolean {
  return patterns.some((pattern) => pattern.test(haystack));
}

function isKaiserCoverage(coverage: Coverage | null | undefined): boolean {
  return /kaiser/.test(coverageSearchText(coverage));
}

function isVaTriWestTricareCoverage(coverage: Coverage | null | undefined): boolean {
  const text = coverageSearchText(coverage);

  return matchesAny(text, [
    /\bva\b/,
    /veterans affairs/,
    /vaccc/,
    /community care network/,
    /triwest/,
    /tri-west/,
    /tricare/,
    /tri-care/,
  ]);
}

function isUhcOptumCoverage(coverage: Coverage | null | undefined): boolean {
  const text = coverageSearchText(coverage);

  return matchesAny(text, [/\bunited\b/, /\buhc\b/, /unitedhealth/, /united health/, /rocky mountain health/, /optum/]);
}

export function resolveUsrcCpsAuthPortalPath({
  subdomain,
  primaryCoverage,
  secondaryCoverage,
}: {
  subdomain: string;
  primaryCoverage: Coverage | null | undefined;
  secondaryCoverage?: Coverage | null | undefined;
}): UsrcCpsAuthPortalPath {
  const coverages = [primaryCoverage, secondaryCoverage].filter(Boolean) as Coverage[];

  if (subdomain === ConfigSubdomain.USRC_CPS_CO && coverages.some((coverage) => isKaiserCoverage(coverage))) {
    return UsrcCpsAuthPortalPath.KaiserCoNoAction;
  }

  if (coverages.some((coverage) => isVaTriWestTricareCoverage(coverage))) {
    return UsrcCpsAuthPortalPath.VaTriWestTricare;
  }

  if (coverages.some((coverage) => isUhcOptumCoverage(coverage))) {
    return UsrcCpsAuthPortalPath.UhcOptum;
  }

  return UsrcCpsAuthPortalPath.Availity;
}

export function buildUsrcCpsReferralAuthInstruction({
  path,
  authOnFile,
}: {
  path: UsrcCpsAuthPortalPath;
  authOnFile: ReferralAuthOnFileSnapshot;
}): UsrcCpsReferralAuthInstructionContent {
  if (authOnFile.hasAuthOnFile) {
    return {
      text: buildReferralAuthOnFileMessage(authOnFile, "Referral authorization is on file."),
      showVaFormLink: false,
    };
  }

  switch (path) {
    case UsrcCpsAuthPortalPath.KaiserCoNoAction:
      return { text: "No immediate action required. Flag in the patient case action note.", showVaFormLink: false };
    case UsrcCpsAuthPortalPath.VaTriWestTricare:
      return {
        text: "Complete the VA Request for Services (RFS) form and upload to Athena.",
        showVaFormLink: true,
      };
    case UsrcCpsAuthPortalPath.UhcOptum:
      return { text: "Obtain referral authorization via the UHC (Optum) portal.", showVaFormLink: false };
    case UsrcCpsAuthPortalPath.Availity:
      return { text: "Obtain referral authorization via Availity.", showVaFormLink: false };
    default: {
      const _exhaustive: never = path;

      return _exhaustive;
    }
  }
}
